/**
 * TCB R40 financing / leasing / payment-terms runtime.
 * Application ≠ approval. Prequalification ≠ approval. Estimates ≠ final terms.
 * Never exposes provider secrets or underwriting payloads.
 */
(function (global) {
  'use strict';

  var CFG = global.TCB_FINANCING_INTELLIGENCE || null;
  var MAX_SKUS = 8;

  function loadConfig(cb) {
    if (!CFG && global.TCB_FINANCING_INTELLIGENCE) CFG = global.TCB_FINANCING_INTELLIGENCE;
    if (CFG) {
      cb(null, CFG);
      return;
    }
    var node = document.querySelector('[data-tcb-financing-runtime-url]');
    var url = node && node.getAttribute('data-tcb-financing-runtime-url');
    if (!url || !global.fetch) {
      cb(new Error('financing_config_missing'));
      return;
    }
    global
      .fetch(url)
      .then(function (r) {
        return r.json();
      })
      .then(function (json) {
        CFG = json;
        global.TCB_FINANCING_INTELLIGENCE = json;
        cb(null, json);
      })
      .catch(function (err) {
        cb(err || new Error('financing_fetch_failed'));
      });
  }

  function getApplication(ref) {
    return (
      ((CFG && CFG.applications) || []).find(function (a) {
        return a.application_ref === ref;
      }) || null
    );
  }

  function isApprovedState(state) {
    return state === 'APPROVED' || state === 'PARTIALLY_APPROVED';
  }

  function normalizeTruth(app) {
    if (!app) return { ok: false, reason: 'unknown_application' };
    var state = app.state;
    return {
      ok: true,
      application_ref: app.application_ref,
      state: state,
      is_application: true,
      is_approval: isApprovedState(state),
      is_prequalified_only: state === 'PREQUALIFIED',
      prequalification_is_approval: false,
      application_is_approval: false,
      estimated_monthly_payment: app.estimated_monthly_payment || null,
      estimated_is_final: false,
      disclaimer: app.estimated_monthly_payment_disclaimer || 'Estimate only — not final provider terms',
      approved_amount: isApprovedState(state) ? app.approved_amount : null,
      expires_at: app.expires_at || null
    };
  }

  function evaluatePaymentOptions(context) {
    context = context || {};
    var amount = Number(context.amount || 0);
    var marketKey = context.market_key || 'us-consumer';
    var skuCount = Number(context.unique_sku_count || 0);
    var options = [];

    function push(type, eligible, reason) {
      options.push({ type: type, eligible: !!eligible, reason: reason || null });
    }

    if (skuCount > MAX_SKUS) {
      push('NOT_ELIGIBLE', true, 'sku_cap_exceeded');
      return { quote_id: context.quote_id || null, options: options, decision_timestamp: new Date().toISOString() };
    }
    if (context.compatibility_ok === false || context.safety_ok === false || context.job_complete === false) {
      push('NOT_ELIGIBLE', true, 'package_gates_failed');
      return { quote_id: context.quote_id || null, options: options, decision_timestamp: new Date().toISOString() };
    }

    // Prefer cached decision when quote matches
    var cached = ((CFG && CFG.payment_option_decisions) || []).find(function (d) {
      return d.quote_id && context.quote_id && d.quote_id === context.quote_id;
    });
    if (cached && cached.options) {
      return {
        quote_id: cached.quote_id,
        options: cached.options.slice(),
        decision_timestamp: cached.decision_timestamp,
        from_cache: true
      };
    }

    push('PAY_NOW', true, null);
    push('B2B_PAYMENT_TERMS', !!(context.company_id && context.company_location_id), 'b2b_context_required');
    push('DEPOSIT_PLUS_BALANCE', !!(context.company_id && amount >= 5000), amount >= 5000 ? 'b2b_context_required' : 'below_deposit_threshold');
    push('EXTERNAL_FINANCING', amount >= 2500, amount >= 2500 ? null : 'below_financing_minimum');
    push('EQUIPMENT_LEASE', amount >= 5000 && context.equipment_eligible !== false, amount >= 5000 ? null : 'below_lease_threshold');
    push('RENTAL_OR_LEASE_QUOTE', !!context.rental_path, context.rental_path ? null : 'rental_not_requested');
    push('MANUAL_FINANCE_REVIEW', true, null);

    // Option precedence: do not hide PAY_NOW
    var precedence = (CFG && CFG.policy && CFG.policy.option_precedence) || [];
    options.sort(function (a, b) {
      var ia = precedence.indexOf(a.type);
      var ib = precedence.indexOf(b.type);
      return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
    });

    return {
      quote_id: context.quote_id || null,
      market_key: marketKey,
      options: options,
      decision_timestamp: new Date().toISOString(),
      from_cache: false
    };
  }

  function canConvertToOrder(applicationRef, context) {
    context = context || {};
    var app = getApplication(applicationRef);
    var truth = normalizeTruth(app);
    if (!truth.ok) return { ok: false, reasons: [truth.reason] };

    var reasons = [];
    if (!truth.is_approval) {
      reasons.push(truth.is_prequalified_only ? 'prequalified_not_approved' : 'application_not_approved');
    }
    if (app.expires_at && Date.parse(app.expires_at) < Date.now()) {
      reasons.push('approval_expired');
    }
    if (context.quote_changed) reasons.push('quote_changed_revalidate');
    if (context.availability_ok === false) reasons.push('availability_revalidation_failed');
    if (context.unique_sku_count > MAX_SKUS) reasons.push('sku_cap_exceeded');
    if (context.compatibility_ok === false) reasons.push('compatibility_failed');
    if (context.safety_ok === false) reasons.push('safety_failed');

    // Market / tax runtimes when present
    if (global.TCBMarketsRuntime && typeof global.TCBMarketsRuntime.evaluatePackageSellability === 'function' && context.package_id) {
      var sell = global.TCBMarketsRuntime.evaluatePackageSellability(
        context.package_id,
        { market_key: app.market_key || context.market_key },
        { unique_sku_count: context.unique_sku_count || 1 }
      );
      if (sell && sell.decision === 'UNAVAILABLE') reasons.push('market_unavailable');
    }

    return {
      ok: reasons.length === 0,
      reasons: reasons,
      application_ref: applicationRef,
      state: app.state,
      live_write: false
    };
  }

  function invalidateOnQuoteChange(applicationRef) {
    var app = getApplication(applicationRef);
    if (!app) return { ok: false, reason: 'unknown_application' };
    track('stale_financing_invalidated', { application_ref: applicationRef, quote_id: app.quote_id });
    return {
      ok: true,
      application_ref: applicationRef,
      state: 'REVIEW_REQUIRED',
      note: 'Quote change invalidates stale financing decision — revalidate before conversion',
      dry_run: true
    };
  }

  function track(eventName, payload) {
    var detail = Object.assign({ event: eventName, at: new Date().toISOString() }, payload || {});
    // Never attach sensitive fields
    if (detail.ssn || detail.ein || detail.credit_report || detail.underwriting) {
      delete detail.ssn;
      delete detail.ein;
      delete detail.credit_report;
      delete detail.underwriting;
    }
    try {
      if (global.TCBAnalytics && typeof global.TCBAnalytics.track === 'function') global.TCBAnalytics.track(eventName, detail);
      global.dispatchEvent(new CustomEvent('tcb:financing', { detail: detail }));
    } catch (e) {
      /* no-op */
    }
  }

  global.TCBFinancingRuntime = {
    version: 'r40.0.0',
    maxSkus: MAX_SKUS,
    loadConfig: loadConfig,
    getApplication: getApplication,
    normalizeTruth: normalizeTruth,
    evaluatePaymentOptions: evaluatePaymentOptions,
    canConvertToOrder: canConvertToOrder,
    invalidateOnQuoteChange: invalidateOnQuoteChange,
    track: track
  };
})(typeof window !== 'undefined' ? window : globalThis);
